import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard, Calendar, CheckCircle2, BarChart3,
  Settings, LogOut, Sun, Moon, Building2, ChevronDown, FileBarChart
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const LOGO_DARK = '/stp-logo.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_stellar-crm-hub/artifacts/pvd4h9i1_White%20and%20Yellow%20with%20name.png';

const clientNavItems = [
  { icon: LayoutDashboard, label: 'Overview', path: '/' },
  { icon: Calendar, label: 'Content Calendar', path: '/calendar' },
  { icon: CheckCircle2, label: 'Approvals', path: '/approvals' },
  { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  { icon: FileBarChart, label: 'Reports', path: '/reports' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspace();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'C';
  const logo = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Client Sidebar */}
      <div className="w-[260px] h-screen flex flex-col bg-card border-r border-border" data-testid="client-sidebar">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 h-14 border-b border-border">
          <img src={logo} alt="Stellar Productions" className="h-8 w-8 object-contain rounded" />
          <div>
            <span className="font-display font-semibold text-sm text-gold">Client Portal</span>
          </div>
        </div>

        {/* Workspace Info */}
        <div className="px-3 py-3">
          <div className="p-3 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gold" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{currentWorkspace?.name || 'Workspace'}</p>
                <p className="text-xs text-muted-foreground">Client Access</p>
              </div>
            </div>
          </div>
        </div>

        {/* Workspace Switcher for clients with multiple workspaces */}
        {workspaces.length > 1 && (
          <div className="px-3 pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-between h-8 px-3 text-xs">
                  Switch Workspace <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[230px]">
                {workspaces.map(ws => (
                  <DropdownMenuItem
                    key={ws.id}
                    onClick={() => switchWorkspace(ws)}
                    className={ws.id === currentWorkspace?.id ? 'bg-secondary' : ''}
                  >
                    {ws.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <Separator />

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2 py-2">
          <nav className="space-y-1">
            {clientNavItems.map(item => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Button
                  key={item.path}
                  variant={isActive ? 'secondary' : 'ghost'}
                  className={`w-full justify-start px-3 h-9 ${
                    isActive ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => navigate(item.path)}
                  data-testid={`client-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`h-4 w-4 mr-3 shrink-0 ${isActive ? 'text-gold' : ''}`} />
                  <span className="text-sm">{item.label}</span>
                </Button>
              );
            })}
          </nav>
        </ScrollArea>

        <Separator />

        {/* Theme + User */}
        <div className="p-3 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start px-3 h-9 text-muted-foreground hover:text-foreground"
            onClick={toggleTheme}
            data-testid="client-theme-toggle"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4 mr-3" />
            ) : (
              <Moon className="h-4 w-4 mr-3" />
            )}
            <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </Button>

          <div className="flex items-center justify-between p-2 rounded-lg">
            <div className="flex items-center gap-2">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-gold/20 text-gold text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
